import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft, LifeBuoy, Mail, Crown, RefreshCw, Settings as SettingsIcon, Tag } from "lucide-react";
import { NotificationsToggle } from "@/components/NotificationSettings";
import { SignInRequired } from "@/components/SignInRequired";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { isNativeStoreApp } from "@/lib/store-billing";
import { openStoreManagement, restoreStorePurchases } from "@/lib/native-billing";
import { trialProgress } from "@/lib/premium";
import { CANCEL_ANYTIME } from "@/lib/trial";
import { toast } from "sonner";

export const Route = createFileRoute("/settings")({
  component: SettingsPage,
  head: () => ({
    meta: [
      { title: "Settings - Inquira" },
      { name: "description", content: "Manage your notification preferences and account settings on Inquira." },
      { property: "og:title", content: "Inquira Settings" },
      { property: "og:description", content: "Manage your notification preferences and account settings on Inquira." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function SettingsPage() {
  const { user, isLoading, isPaid, planPriceId, premium, refreshPremium, refreshUsage } = useAuth();
  const [busy, setBusy] = useState<null | "restore" | "manage">(null);
  const native = isNativeStoreApp();
  const trial = premium ? trialProgress(premium) : null;
  const onTrial = trial?.active ?? false;
  const planLabel = !isPaid
    ? "Free"
    : onTrial
      ? "Premium Trial"
      : planPriceId === "inquira_pro_yearly"
        ? "Premium Yearly"
        : "Premium Monthly";
  const fmtDate = (value: string | null | undefined) =>
    value ? new Date(value).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;
  const renewalDate = fmtDate(
    onTrial ? premium?.trial_ends_at : premium?.current_period_end ?? premium?.subscription_expires_at,
  );
  const renewalPrice = planPriceId === "inquira_pro_yearly" ? "$99.99/year" : "$9.99/month";

  const restore = async () => {
    setBusy("restore");
    try {
      const result = await restoreStorePurchases();
      await refreshPremium();
      await refreshUsage();
      toast[result.premium ? "success" : "info"](
        result.premium ? "Purchases restored — Premium is active." : "No active subscription was found.",
      );
    } catch {
      toast.error("Couldn't restore purchases. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const manage = async () => {
    setBusy("manage");
    try {
      const opened = await openStoreManagement();
      if (!opened) window.location.href = "/subscription";
    } finally {
      setBusy(null);
    }
  };

  if (isLoading) return <div className="p-8 text-center">Loading…</div>;
  if (!user) {
    return (
      <SignInRequired
        title="Sign in to manage settings"
        description="Your notification preferences and account settings live here."
      />
    );
  }
  return (
    <div className="mx-auto max-w-2xl p-4 sm:p-6">
      <Link to="/" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground">
        <ArrowLeft className="h-4 w-4" /> Home
      </Link>
      <h1 className="mb-6 text-3xl font-bold">Settings</h1>
      <section className="mb-8 space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-widest text-ink-muted">Subscription</h2>
        <div className="rounded-2xl border border-rule bg-card p-4">
          <div className="flex items-center gap-2">
            {isPaid && <Crown className="h-4 w-4 text-neon-pink" />}
            <span className="font-semibold">Current plan: {planLabel}</span>
          </div>
          {isPaid && (
            <dl className="mt-2 space-y-1 text-sm text-ink-muted">
              <div>
                <dt className="inline font-semibold text-ink">Status: </dt>
                <dd className="inline">
                  {onTrial
                    ? "Premium Trial"
                    : premium?.cancel_at_period_end
                      ? "Canceled — access until period end"
                      : "Active"}
                </dd>
              </div>
              {renewalDate && (
                <div>
                  <dt className="inline font-semibold text-ink">
                    {onTrial ? "Trial ends: " : premium?.cancel_at_period_end ? "Access ends: " : "Next billing date: "}
                  </dt>
                  <dd className="inline">{renewalDate}</dd>
                </div>
              )}
              <div>
                <dt className="inline font-semibold text-ink">Renewal price: </dt>
                <dd className="inline">{renewalPrice}</dd>
              </div>
            </dl>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/pricing">
              <Button variant="subtle"><Tag className="size-4" /> View Plans</Button>
            </Link>
            <Link to="/subscription">
              <Button variant="scan">
                <SettingsIcon className="size-4" /> Manage or Cancel Subscription
              </Button>
            </Link>
            {isPaid && native && (
              <Button variant="subtle" onClick={manage} disabled={busy !== null}>
                <SettingsIcon className="size-4" /> Open Apple subscription settings
              </Button>
            )}
            {native && (
              <Button variant="subtle" onClick={restore} disabled={busy !== null}>
                <RefreshCw className="size-4" /> {busy === "restore" ? "Restoring…" : "Restore Purchases"}
              </Button>
            )}
          </div>
          <p className="mt-3 text-xs font-semibold text-ink">{CANCEL_ANYTIME}</p>
        </div>
      </section>
      <section className="space-y-4">
        <h2 className="text-sm font-bold uppercase tracking-widest text-ink-muted">Notifications</h2>
        <NotificationsToggle />
      </section>
      <section className="mt-8 space-y-3">
        <h2 className="text-sm font-bold uppercase tracking-widest text-ink-muted">Help & Support</h2>
        <Link
          to="/support"
          className="flex items-center gap-3 rounded-2xl border border-rule bg-card p-4 hover:bg-rule/20"
        >
          <LifeBuoy className="h-5 w-5 text-primary" />
          <div>
            <div className="font-semibold">Visit the Help Center</div>
            <div className="text-sm text-ink-muted">FAQs, contact form, and support chat</div>
          </div>
        </Link>
        <a
          href="mailto:info@inquira.online"
          className="flex items-center gap-3 rounded-2xl border border-rule bg-card p-4 hover:bg-rule/20"
        >
          <Mail className="h-5 w-5 text-primary" />
          <div>
            <div className="font-semibold">Email support</div>
            <div className="text-sm text-ink-muted">info@inquira.online</div>
          </div>
        </a>
      </section>
    </div>
  );
}